import { AddressElement, useElements } from '@stripe/react-stripe-js';
import { forwardRef, useCallback, useImperativeHandle } from 'react';
import type { Address, NewAddress, OnSubmit } from 'types/common';
import styles from './UpdateBillingAddressForm.module.css';

interface BillingAddressFormProps {
  existingAddress?: Address;
  onSubmit: (address?: NewAddress) => void;
  onCancel: () => void;
}

const BillingAddressForm = forwardRef<OnSubmit, BillingAddressFormProps>(
  ({ existingAddress, onSubmit, onCancel }, ref) => {
    const elements = useElements();

    const handleSubmit = useCallback(async () => {
      if (!elements) {
        onSubmit(undefined);
        return;
      }
      const addressElement = elements.getElement('address');

      if (!addressElement) {
        onSubmit(undefined);
        return;
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        onSubmit(undefined);
        return;
      }

      const { complete, value } = await addressElement.getValue();

      if (complete) {
        onSubmit({
          name: value.name,
          ...value.address,
        });
        return;
      }

      onSubmit(undefined);
    }, [elements, onSubmit]);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit: handleSubmit,
      }),
      [handleSubmit]
    );

    const focusAddress = useCallback(() => {
      elements?.getElement('address')?.focus();
    }, [elements]);

    const addressOptions: GetReactParameterBag<typeof AddressElement>['options'] = {
      mode: 'billing',
      autocomplete: {
        mode: 'google_maps_api',
        apiKey: process.env.VITE_GOOGLE_MAPS_API_KEY || '',
      },
      defaultValues: existingAddress?.country
        ? {
            address: {
              line1: existingAddress?.line1,
              line2: existingAddress?.line2,
              city: existingAddress?.city,
              state: existingAddress?.state,
              postal_code: existingAddress?.zip,
              country: existingAddress?.country ?? '',
            },
            name: existingAddress?.name,
          }
        : undefined,
      display: { name: 'organization' },
    };

    return (
      <div className={styles.wrapper}>
        <AddressElement options={addressOptions} onReady={focusAddress} onEscape={onCancel} />
      </div>
    );
  }
);

export default BillingAddressForm;
